import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FILTERED_GREEN_BUTTON } from '../constants/colors';
import { PRAIRI_SURFACES } from '../constants/prairiSurfaces';

interface StartupReviewCalloutProps {
  onDismiss: () => void;
}

/** Offset so the tip reads as educational, not part of the tracker chrome. */
const ANGLE_OFFSET_X = 14;
const ANGLE_OFFSET_Y = -18;

function calloutPosition(): { left: number; top: number } {
  const width = Math.min(352, window.innerWidth - 32);
  const maxLeft = Math.max(16, window.innerWidth - width - 16);
  const follow = document.querySelector('[data-follow-action]');
  if (follow) {
    const rect = follow.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      return {
        left: Math.min(Math.max(16, rect.right + ANGLE_OFFSET_X), maxLeft),
        top: Math.max(72, rect.top + rect.height / 2 - 36 + ANGLE_OFFSET_Y),
      };
    }
  }
  return { left: 24, top: 96 };
}

/**
 * First-review note beside Follow.
 */
const StartupReviewCallout: React.FC<StartupReviewCalloutProps> = ({ onDismiss }) => {
  const [position, setPosition] = useState(calloutPosition);

  useEffect(() => {
    const update = () => setPosition(calloutPosition());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1000]" onClick={onDismiss} aria-hidden />
      <div
        role="dialog"
        aria-label="Your progress tracker"
        className="fixed z-[1001] w-[min(22rem,calc(100vw-2rem))]"
        style={{
          left: position.left,
          top: position.top,
          transform: 'rotate(1.5deg)',
        }}
        onClick={event => event.stopPropagation()}
      >
        <div
          className="relative rounded-2xl bg-white px-4 py-3 shadow-xl"
          style={{ color: PRAIRI_SURFACES.page, fontFamily: 'Rubik, sans-serif' }}
        >
          <span aria-hidden className="absolute -left-2 top-8 h-4 w-4 rotate-45 bg-white" />
          <div className="relative flex items-end gap-3">
            <p className="flex-1 text-[15px] leading-snug">
              Your progress tracker further refines your matches and feeds.
            </p>
            <button
              type="button"
              onClick={onDismiss}
              className={`shrink-0 rounded-full px-4 py-1 text-sm font-medium ${FILTERED_GREEN_BUTTON}`}
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default StartupReviewCallout;
